import type { CSSProperties, ReactNode } from 'react';

export function formatVN(value: number, minDec = 0, maxDec = 2): string {
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: minDec,
    maximumFractionDigits: maxDec,
  }).format(value);
}

export function formatSigned(value: number, currency: string, decimals = 2): string {
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatVN(Math.abs(value), decimals, decimals)} ${currency}`;
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-700">{children}</span>
      {hint && <span className="font-mono text-xs font-medium text-slate-400">{hint}</span>}
    </div>
  );
}

export function NumberInput({
  value,
  onChange,
  suffix,
  min,
  step,
}: {
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  min?: number;
  step?: number;
}) {
  return (
    <div className="relative">
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="tab-focus w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-16 font-mono text-base font-semibold text-slate-800 outline-none"
      />
      {suffix && (
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">
          {suffix}
        </span>
      )}
    </div>
  );
}

export function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="tab-focus w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 font-mono text-base font-semibold text-slate-800 outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
    </div>
  );
}

export function QuickChips({
  options,
  value,
  onChange,
  format,
}: {
  options: number[];
  value: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = Math.abs(o - value) < 0.001;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              active ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={active ? { backgroundColor: 'var(--tab-accent)' } : undefined}
          >
            {format ? format(o) : o}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex rounded-xl bg-slate-100 p-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stat({
  label,
  value,
  accent,
  valueClassName = '',
}: {
  label: string;
  value: string;
  accent?: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3.5">
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p
        className={`mt-1 font-mono text-sm font-bold tabular-nums ${valueClassName}`}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

export function Slider({
  value,
  onChange,
  min,
  max,
  step,
  accent = '#7c3aed',
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  accent?: string;
}) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      style={
        {
          '--fill': `${percent}%`,
          '--slider-accent': accent,
          '--slider-track': '#e2e8f0',
        } as CSSProperties
      }
    />
  );
}

export function NextStep({ title, desc, cta, onClick }: { title: string; desc: string; cta: string; onClick: () => void }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex items-start gap-3.5">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--tab-accent) 12%, white)',
            color: 'var(--tab-accent)',
          }}
        >
          <i className="ri-arrow-right-up-line text-xl" />
        </span>
        <div>
          <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">{desc}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        className="group flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full py-2.5 pl-5 pr-2.5 text-sm font-semibold text-white transition-all hover:brightness-110"
        style={{ backgroundColor: 'var(--tab-accent)' }}
      >
        {cta}
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
          <i className="ri-arrow-right-line text-sm" />
        </span>
      </button>
    </div>
  );
}